import { useKestrel } from 'features/kestrelViewer/KestrelProvider'
import { useKestrelViewerContext } from 'features/kestrelViewer/types/KestrelViewerInterfaces'
import { AnnotationMenu } from 'features/kestrelViewer/ui/popupMenu/annotationMenu/AnnotationMenu'
import React from 'react'
import {
  isKVWorkOrdersSelection,
  KVAnnotation,
  KVCameraView,
  KVInspectionArea,
  KVMeasurement,
  KVPrimitive,
} from 'types'

import { CameraViewMenu } from './cameraViewMenu/CameraViewMenu'
import { Form781AMenu } from './form781A/Form781AMenu'
import { InspectionAreaMenu } from './inspectionAreaMenu/InspectionAreaMenu'
import { MeasurementMenu } from './measurementMenu/MeasurementMenu'
import { PrimitiveMenu } from './primitiveMenu/PrimitiveMenu'
import { MarkupPopupContainer } from './shared/MarkupPopupContainer'

const isAnnotation = (
  markup: unknown,
  primitiveGeometryAsAnnotation: boolean
): markup is KVAnnotation => {
  if (!primitiveGeometryAsAnnotation) {
    return markup instanceof KVAnnotation
  }

  // default behavior: use geometry property to identify annotations
  return markup !== null && typeof markup === 'object' && 'geometry' in markup
}

export const MarkupPopupMenu: React.FC = () => {
  const { gatekeeper } = useKestrel()
  const {
    setSelectedMarkup,
    selectedMarkup,
    popupMenuStartPosition,
    setHelperTextVisible,
  } = useKestrelViewerContext()

  const selectedAnnotation = isAnnotation(
    selectedMarkup,
    gatekeeper.feature.primitiveGeometryAsAnnotation
  )
    ? selectedMarkup
    : undefined

  const selectedWorkOrders = isKVWorkOrdersSelection(selectedMarkup)
    ? selectedMarkup
    : undefined

  const selectedInspectionArea =
    selectedMarkup instanceof KVInspectionArea
      ? (selectedMarkup as KVInspectionArea)
      : undefined

  const selectedCameraView =
    selectedMarkup instanceof KVCameraView
      ? (selectedMarkup as KVCameraView)
      : undefined

  const selectedMeasurement =
    selectedMarkup instanceof KVMeasurement
      ? (selectedMarkup as KVMeasurement)
      : undefined

  const selectedPrimitive =
    selectedMarkup instanceof KVPrimitive
      ? (selectedMarkup as KVPrimitive)
      : undefined

  // Hide helper text after render when an annotation is selected to avoid setState during render
  React.useEffect(() => {
    if (selectedAnnotation) {
      setHelperTextVisible(false)
    }
  }, [selectedAnnotation, setHelperTextVisible])

  // Don't render if no popup position or no selected markup
  if (!popupMenuStartPosition || !selectedMarkup) {
    return null
  }

  // For annotations, render the new modal-style menu
  if (selectedAnnotation) {
    return <AnnotationMenu selectedAnnotation={selectedAnnotation} />
  }

  // For work orders, render with its own container
  if (selectedWorkOrders) {
    return (
      <Form781AMenu
        kestrelModelId={selectedWorkOrders.kestrelModelId}
        workOrders={selectedWorkOrders.workOrders}
      />
    )
  }

  // For other markup types, render shared popup container
  return (
    <MarkupPopupContainer
      popupMenuStartPosition={popupMenuStartPosition}
      setSelectedMarkup={setSelectedMarkup}>
      {selectedCameraView && (
        <CameraViewMenu selectedCameraView={selectedCameraView} />
      )}
      {selectedInspectionArea && (
        <InspectionAreaMenu selectedInspectionArea={selectedInspectionArea} />
      )}
      {selectedMeasurement && (
        <MeasurementMenu selectedMeasurement={selectedMeasurement} />
      )}
      {selectedPrimitive && (
        <PrimitiveMenu selectedPrimitive={selectedPrimitive} />
      )}
    </MarkupPopupContainer>
  )
}
